import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import { toast } from "react-toastify";
import "../../assets/styles/AdminShared.css";

const statusOptions = ["new", "reviewing", "in-progress", "waiting-on-client", "quoted-separately", "done"];

const AdminCarePlanRequestDetail = () => {
  const { projectId, requestId } = useParams();
  const [projectName, setProjectName] = useState("");
  const [requests, setRequests] = useState([]);
  const [requestIndex, setRequestIndex] = useState(-1);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const snap = await getDoc(doc(db, "projects", projectId));
        if (!snap.exists()) throw new Error("Project not found.");

        const data = snap.data();
        const decodedRequestId = decodeURIComponent(requestId);
        const nextRequests = data.carePlanRequests || [];
        const index = nextRequests.findIndex((item, i) =>
          item.id === decodedRequestId || item.submittedAt === decodedRequestId || String(i) === decodedRequestId
        );

        setProjectName(data.projectName || "Website Project");
        setRequests(nextRequests);
        setRequestIndex(index);
      } catch (error) {
        toast.error("Failed to load request.");
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [projectId, requestId]);

  const request = requests[requestIndex];
  const messages = request?.messages?.length
    ? request.messages
    : request
      ? [{
          id: "initial-message",
          author: "client",
          body: request.details,
          createdAt: request.submittedAt,
        }]
      : [];

  const saveUpdatedRequests = async (updatedRequests, successMessage) => {
    const cleanedRequests = updatedRequests.map(({ adminNotes, ...request }) => request);
    await updateDoc(doc(db, "projects", projectId), {
      carePlanRequests: cleanedRequests,
      latestCarePlanRequest: cleanedRequests[cleanedRequests.length - 1] || null,
    });
    setRequests(cleanedRequests);
    toast.success(successMessage);
  };

  const handleStatusChange = async (status) => {
    try {
      const updatedRequests = requests.map((item, index) =>
        index === requestIndex ? { ...item, status } : item
      );
      await saveUpdatedRequests(updatedRequests, "Request status updated.");
    } catch (error) {
      toast.error("Failed to update status.");
    }
  };

  const handleSendReply = async () => {
    if (!reply.trim() || requestIndex < 0) return;

    try {
      setSaving(true);
      const nextMessage = {
        id: `msg-${Date.now()}`,
        author: "admin",
        body: reply.trim(),
        createdAt: new Date().toISOString(),
      };
      const updatedRequests = requests.map((item, index) =>
        index === requestIndex
          ? { ...item, messages: [...messages, nextMessage] }
          : item
      );

      await saveUpdatedRequests(updatedRequests, "Reply sent.");
      setReply("");
    } catch (error) {
      toast.error("Failed to send reply.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ap-loading"><p>Loading request...</p></div>;

  if (!request) {
    return (
      <div className="ap-page ap-page--narrow">
        <div className="ap-page-header">
          <a href={`/admin/project/${projectId}/care-plan-requests`} className="ap-back">← Back to requests</a>
          <p className="ap-eyebrow">Admin — Care Plan</p>
          <h1 className="ap-title">Request Not Found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-page ap-page--narrow">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}/care-plan-requests`} className="ap-back">← Back to requests</a>
        <p className="ap-eyebrow">Admin — Care Plan</p>
        <h1 className="ap-title">{projectName}: {request.requestType || "Request"}</h1>
        <p className="ap-subtitle">{request.pageOrFeature || "No page or feature specified"}</p>
      </div>

      <div className="ap-section" style={{ marginBottom: "20px" }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Request Details</h2>
          <span className={`ap-badge ${request.status === "done" ? "ap-badge--active" : "ap-badge--pending"}`}>{request.status || "new"}</span>
        </div>
        <div className="ap-section-body">
          <div className="ap-form-group">
            <label>Status</label>
            <select value={request.status || "new"} onChange={e => handleStatusChange(e.target.value)}>
              {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>
          <ul className="ap-checklist">
            <li className="ap-checklist-item"><span><strong>Priority:</strong> {request.priority || "Normal"}</span></li>
            <li className="ap-checklist-item"><span><strong>Preferred deadline:</strong> {request.deadline || "Not provided"}</span></li>
            <li className="ap-checklist-item"><span><strong>Submitted:</strong> {request.submittedAt ? new Date(request.submittedAt).toLocaleString() : "Not available"}</span></li>
          </ul>
          <p style={{ fontFamily: "var(--font-body)", fontSize: "0.9375rem", color: "var(--text-secondary)", lineHeight: 1.7, margin: "16px 0 0" }}>
            {request.details}
          </p>
        </div>
      </div>

      <div className="ap-section">
        <div className="ap-section-header">
          <h2 className="ap-section-title">Conversation</h2>
        </div>
        <div className="ap-section-body">
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
            {messages.map((item) => (
              <div key={item.id || item.createdAt} style={{
                alignSelf: item.author === "admin" ? "flex-end" : "flex-start",
                maxWidth: "82%",
                background: item.author === "admin" ? "rgba(245, 78, 0, 0.08)" : "var(--surface-200)",
                color: "var(--text-primary)",
                border: item.author === "admin" ? "1px solid rgba(245, 78, 0, 0.22)" : "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                padding: "12px 14px",
              }}>
                <p style={{ fontFamily: "var(--font-mono)", fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.75, margin: "0 0 6px" }}>
                  {item.author === "admin" ? "You" : "Client"} {item.createdAt ? `· ${new Date(item.createdAt).toLocaleString()}` : ""}
                </p>
                <p style={{ fontFamily: "var(--font-ui)", fontSize: "0.9375rem", lineHeight: 1.6, margin: 0 }}>{item.body}</p>
              </div>
            ))}
          </div>

          <div className="ap-form-group">
            <label>Reply to Client</label>
            <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Ask a question, give an update, or confirm completion." />
          </div>
          <div className="ap-btn-row">
            <button className="ap-btn-primary" onClick={handleSendReply} disabled={saving || !reply.trim()}>
              {saving ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCarePlanRequestDetail;
